/**
 * Placement: the same affine transform the plugin runs, on a tangent plane.
 *
 * A port of `place_tool.py`'s `_draw_at` and `layers.py`'s `place_tract`,
 * which between them do exactly one thing: take the server's local-plane
 * vertices (POB at the origin, feet, y north), offset them by the tract's tie
 * offset, rotate the assembly, and drop it on the clicked point. Nothing here
 * computes geometry. The vertex list is the server's and this file only moves
 * it, which is the same division of labour that lets the plugin ship no
 * traverse math.
 *
 * QGIS scales feet into the project CRS's units with `QgsUnitTypes`. A
 * browser map has no projected CRS to scale into, so the web version converts
 * feet to degrees on a local tangent plane at the click latitude. At parcel
 * scale that is the same approximation the certificate's own figure draws on
 * (`certificate.py` letterboxes a local plane) and the same one `plss.py`
 * uses for aliquot acreage, so it is not a new liberty.
 *
 * `test/place.test.ts` checks this against fixtures generated by the plugin's
 * own math, which is what makes "web and QGIS place the same parcel" a fact
 * rather than an intention.
 */

import type { Call, ServerVerdict, Tract } from "./types";

/** Feet per degree of latitude. WGS84 mean; the error at parcel scale is
 *  centimetres, well inside the linework a county publishes. */
export const FEET_PER_DEGREE_LAT = 364_566.93;

export interface LngLat {
  lng: number;
  lat: number;
}

/** One tract ready to draw: its tie offset from the shared monument, and the
 *  vertex list the server computed. A single tract is a group of one with a
 *  zero offset, which is how `place_tool.py` models it too. */
export interface Ring {
  tract: Tract;
  verdict: ServerVerdict;
  offset: [number, number];
  vertices: [number, number][];
}

/**
 * Feet east/north of an origin, as a lon/lat pair.
 *
 * Longitude degrees shrink with the cosine of latitude; ignoring that is the
 * classic way a parcel comes out stretched east-west, by 24% at the latitude
 * of Denver.
 */
export function offsetToLngLat(
  origin: LngLat,
  eastFt: number,
  northFt: number,
): LngLat {
  const latRad = (origin.lat * Math.PI) / 180;
  const feetPerDegreeLng = FEET_PER_DEGREE_LAT * Math.cos(latRad);
  return {
    lng: origin.lng + eastFt / feetPerDegreeLng,
    lat: origin.lat + northFt / FEET_PER_DEGREE_LAT,
  };
}

/**
 * Rotate a local-plane point about the origin.
 *
 * **Counter-clockwise positive**, which is worth stating because the instinct
 * is the other way: this plane is x-east, y-north, so the standard maths
 * convention applies and a positive angle turns the parcel anticlockwise on a
 * north-up map. Due north at +90° comes out due west, not east.
 *
 * The convention is the plugin's, not a choice made here. `_draw_at` builds
 * `x·cos − y·sin, x·sin + y·cos` and the dock's slider is labelled in degrees
 * of that sense, so flipping the sign would make the browser and QGIS
 * disagree about which way 2° turns a parcel. That is the class of difference
 * nobody notices until a deliverable is wrong, which is why
 * `test/place.test.ts` pins it against the plugin's own output.
 */
export function rotate(
  x: number,
  y: number,
  degrees: number,
): [number, number] {
  const theta = (degrees * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return [x * cos - y * sin, x * sin + y * cos];
}

/** How far a drawn polyline may depart the true arc, in feet. Mirrors
 *  `traverse.arc_points`; the two samplers must agree or web and QGIS stop
 *  drawing the same parcel. */
const MAX_SAGITTA_FT = 0.25;
const MAX_ARC_POINTS = 128;

/**
 * Intermediate points along a curve call's arc, endpoints excluded.
 *
 * A TS mirror of `groundtruth_core.traverse.arc_points`, because a curve
 * drawn as its bare chord misstates the boundary by the middle ordinate:
 * 22.7 feet on the customer-reported 1,635.67-foot highway curve. Empty for
 * anything that is not a real arc (no radius, no direction, or a radius
 * shorter than half the chord), so the chord fallback engages.
 */
export function arcPoints(
  start: [number, number],
  end: [number, number],
  radius: number,
  direction: string,
): [number, number][] {
  const [x0, y0] = start;
  const [x1, y1] = end;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const chord = Math.hypot(dx, dy);
  if (chord === 0 || radius < chord / 2) return [];
  const offset = Math.sqrt(Math.max(radius ** 2 - (chord / 2) ** 2, 0));
  // The traveler's left; a left turn keeps its center on that side.
  const sign = direction.toLowerCase() === "left" ? 1 : -1;
  const cx = (x0 + x1) / 2 + (sign * offset * -dy) / chord;
  const cy = (y0 + y1) / 2 + (sign * offset * dx) / chord;
  const theta = 2 * Math.asin(Math.min(1, chord / (2 * radius)));
  if (theta <= 0) return [];
  const ratio = Math.max(0, 1 - MAX_SAGITTA_FT / radius);
  const maxHalf = ratio < 1 ? Math.acos(Math.min(1, ratio)) : 0;
  const n = Math.max(
    1,
    Math.min(
      maxHalf <= 0 ? MAX_ARC_POINTS : Math.ceil(theta / (2 * maxHalf)),
      MAX_ARC_POINTS,
    ),
  );
  if (n === 1) return [];
  const a0 = Math.atan2(y0 - cy, x0 - cx);
  const sweep = sign * theta;
  const points: [number, number][] = [];
  for (let k = 1; k < n; k += 1) {
    const a = a0 + (sweep * k) / n;
    points.push([cx + radius * Math.cos(a), cy + radius * Math.sin(a)]);
  }
  return points;
}

function courseArc(
  calls: Call[] | undefined,
  vertices: [number, number][],
  index: number,
): [number, number][] {
  const call = calls?.[index];
  if (
    !call ||
    call.call_type !== "curve" ||
    !call.radius ||
    (call.curve_direction !== "left" && call.curve_direction !== "right")
  )
    return [];
  return arcPoints(
    vertices[index],
    vertices[index + 1],
    call.radius,
    call.curve_direction,
  );
}

/** The boundary as actually walked: chord vertices with every curve call
 *  swelled to its arc, in the local plane where the sagitta cap means feet. */
export function walkedPath(ring: Ring): [number, number][] {
  const calls = ring.tract.calls;
  const path: [number, number][] = [ring.vertices[0]];
  for (let i = 0; i + 1 < ring.vertices.length; i += 1) {
    path.push(...courseArc(calls, ring.vertices, i), ring.vertices[i + 1]);
  }
  return path;
}

function project(
  ring: Ring,
  origin: LngLat,
  rotationDeg: number,
  [xFt, yFt]: [number, number],
): [number, number] {
  // Offset and ring rotate together, so a group turns as one assembly about
  // the monument rather than each tract spinning on its own POB.
  const [east, north] = rotate(
    xFt + ring.offset[0],
    yFt + ring.offset[1],
    rotationDeg,
  );
  const { lng, lat } = offsetToLngLat(origin, east, north);
  return [lng, lat];
}

/**
 * Place one ring: densify curves, offset, rotate, and project onto the map.
 *
 * Returns a closed GeoJSON ring. The server's vertex list has one point per
 * call endpoint plus the start, so a closing traverse already returns to its
 * own beginning; a traverse that misses closure gets closed here, the same
 * decision `layers.place_tract` makes with its `misclosure < 1e-9` check.
 */
export function placeRing(
  ring: Ring,
  origin: LngLat,
  rotationDeg: number,
): [number, number][] {
  const points = walkedPath(ring).map((p) =>
    project(ring, origin, rotationDeg, p),
  );
  const first = points[0];
  const last = points[points.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    points.push([first[0], first[1]]);
  }
  return points;
}

/** The per-course polylines, so each call can carry its own attributes into
 *  the export the way the plugin's call-line layer does. A curve's polyline
 *  walks its arc; a line stays two points. */
export function placeSegments(
  ring: Ring,
  origin: LngLat,
  rotationDeg: number,
): [number, number][][] {
  const segments: [number, number][][] = [];
  for (let i = 0; i + 1 < ring.vertices.length; i += 1) {
    const course = [
      ring.vertices[i],
      ...courseArc(ring.tract.calls, ring.vertices, i),
      ring.vertices[i + 1],
    ];
    segments.push(course.map((p) => project(ring, origin, rotationDeg, p)));
  }
  return segments;
}

/**
 * Where one ring's own point of beginning landed, in lon/lat.
 *
 * A group placement is one click and one origin, but every tract in it sits
 * at its own offset from the shared monument, so this is what has to be
 * recorded per tract: saving the monument alone would restore the whole
 * assembly stacked on top of itself. For a single tract the offset is zero
 * and this returns the origin unchanged.
 */
export function pobOf(ring: Ring, origin: LngLat, rotationDeg: number): LngLat {
  const [east, north] = rotate(ring.offset[0], ring.offset[1], rotationDeg);
  return offsetToLngLat(origin, east, north);
}

/** A tract the server already located: the PLSS fabric supplies lon/lat, so
 *  there is nothing to click and nothing to rotate. */
export function georefRings(verdict: ServerVerdict): [number, number][][] {
  return (verdict.georef?.rings ?? []).map((ring) => {
    const closed = ring.slice();
    const first = closed[0];
    const last = closed[closed.length - 1];
    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
      closed.push(first);
    }
    return closed;
  });
}

/**
 * Tracts that can be placed together, keyed by the monument they share.
 *
 * Grouping is a string comparison on the server's normalized anchor text,
 * exactly as `review_dock.py` does it, and exactly as conservatively: two
 * tracts group only when the document used the same words for the monument.
 * A group of one is not a group.
 */
export function groupsByAnchor(
  tracts: Tract[],
  verdicts: ServerVerdict[],
): Map<string, Ring[]> {
  const groups = new Map<string, Ring[]>();
  tracts.forEach((tract, index) => {
    const verdict = verdicts[index];
    if (!verdict?.anchor || !verdict.tie_offset || !isPlaceable(verdict))
      return;
    const rings = groups.get(verdict.anchor) ?? [];
    rings.push({
      tract,
      verdict,
      offset: verdict.tie_offset,
      vertices: verdict.geometry!.vertices,
    });
    groups.set(verdict.anchor, rings);
  });
  for (const [anchor, rings] of groups) {
    if (rings.length < 2) groups.delete(anchor);
  }
  return groups;
}

/** The server decides traversability; a verdict without geometry is that
 *  decision, and its reasons are what the operator needs to see instead. */
export function isPlaceable(verdict: ServerVerdict): boolean {
  return !!verdict.geometry && verdict.geometry.vertices.length >= 2;
}

export function singleRing(tract: Tract, verdict: ServerVerdict): Ring {
  return {
    tract,
    verdict,
    offset: [0, 0],
    vertices: verdict.geometry?.vertices ?? [],
  };
}

/** A bounding box in lon/lat, for fitting the map to what was just placed. */
export function boundsOf(
  rings: [number, number][][],
): [[number, number], [number, number]] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  }
  if (!Number.isFinite(west)) return null;
  return [
    [west, south],
    [east, north],
  ];
}
